import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

interface CountUpAnimationProps {
  end: number;
  duration?: number;
  delay?: number;
  className?: string;
  decimals?: number;
  suffix?: string;
}

/**
 * @param end - The final number to count up to
 * @param duration - Duration of the animation in seconds
 * @param delay - Delay before starting the animation in seconds
 * @param className - Optional CSS class name
 * @param decimals - Number of decimal places to display
 * @param suffix - Optional suffix
 */
const CountUpAnimation: React.FC<CountUpAnimationProps> = ({
  end,
  duration = 1.5,
  delay = 0.2,
  className = '',
  decimals = 0,
  suffix = ''
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const animationRef = useRef<number | null>(null);
  
  useEffect(() => {
    setCount(0);
    
    return () => {
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current);
      }
    };
  }, [end]);

  useEffect(() => {
    if (!isInView) return;  
    
    setCount(0);
    let startTimestamp: number | null = null;
    
    const step = (timestamp: number) => {
      if (!startTimestamp) {
        startTimestamp = timestamp + delay * 1000;
      }
      
      const elapsed = timestamp - startTimestamp;
      
      if (elapsed >= 0) {
        const progress = Math.min(elapsed / (duration * 1000), 1);
        const easedProgress = easeOutExpo(progress);
        const currentCount = Math.min(easedProgress * end, end);
        
        setCount(currentCount);
      }
      
      if (elapsed < duration * 1000) {
        animationRef.current = window.requestAnimationFrame(step);
      } else {
        setCount(end);
        animationRef.current = null;
      }
    };
    
    animationRef.current = window.requestAnimationFrame(step);
    
    return () => {
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isInView, end, duration, delay]);

  const easeOutExpo = (x: number): number => {
    return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
  };

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {count.toFixed(decimals)}{suffix}
    </motion.span>
  );
};

export default CountUpAnimation; 